import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import statisticService from '../../services/statistics';
import { DEFAULT_CURRENCY } from '../../configs/app-global';
import {
  normalizeSalesChartList,
  normalizeTopCustomersList,
  normalizeTopProductsList,
  unwrapApiList,
} from '../../helpers/normalizeDashboardLists';

const DEFAULT_PARAMS = {
  time: 'subMonth',
  granularity: 'daily',
  perPage: 5,
  // Asks the backend for the previous-window aggregates that drive every
  // "vs previous period" line. Older backends ignore the param and simply
  // return no `deltas`, which the tiles render as "no comparison" — not zero.
  compare: 'previous',
};

const DEFAULT_STATE = {
  scope: {},
  filters: {},
  meta: {
    currency: {
      code: DEFAULT_CURRENCY.title,
      symbol: DEFAULT_CURRENCY.symbol,
    },
  },
  cards: {
    big4: {
      totalRevenue: 0,
      totalOrders: 0,
      activeCustomers: 0,
      totalProducts: 0,
    },
    status: {
      inProgress: 0,
      cancelled: 0,
      delivered: 0,
      outOfStock: 0,
    },
    ops: {
      pendingRepairs: 0,
      overdueRepairs: 0,
      openTradeIns: 0,
      awaitingShipment: 0,
    },
  },
  deltas: {},
  previous: {},
  trends: {},
  attention: [],
  charts: {
    salesArea: [],
    ordersBar: [],
  },
  lists: {
    topSoldProducts: [],
    topCustomersLtv: [],
  },
  activity: {
    recent: [],
  },
  todos: [],
  snapshotNote: '',
  legacy: {},
};

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * A KPI may arrive as a bare number (today) or as `{ value, series }` once the
 * backend attaches trend buckets. Read both without the callers caring.
 */
function metricValue(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return toNumber(raw.value ?? raw.total ?? raw.count);
  }
  return toNumber(raw);
}

function metricSeries(raw, fallback) {
  const candidate = Array.isArray(raw?.series) ? raw.series : fallback;
  if (!Array.isArray(candidate)) return [];
  return candidate
    .map((point) =>
      point && typeof point === 'object'
        ? toNumber(point.value ?? point.total ?? point.count)
        : toNumber(point),
    )
    .filter((point) => Number.isFinite(point));
}

/**
 * Signed percentage change, or `null` when there is nothing to compare against.
 * Returning null (not 0) matters: a tile must be able to say "no comparison"
 * rather than claiming the period was flat.
 */
function percentDelta(current, previous) {
  const now = Number(current);
  const before = Number(previous);
  if (!Number.isFinite(now) || !Number.isFinite(before)) return null;
  if (before === 0) return now === 0 ? 0 : null;
  return ((now - before) / Math.abs(before)) * 100;
}

/**
 * Deltas the backend sent, else deltas derived from the `previous` block it
 * sent, else nothing. Explicit server values always win — they know how the
 * window was bounded.
 */
function resolveDeltas(data, currentBig4) {
  const sent = data?.deltas;
  if (sent && typeof sent === 'object') {
    return Object.fromEntries(
      Object.entries(sent).map(([key, value]) => {
        const parsed = Number(
          value && typeof value === 'object' ? value.percent ?? value.value : value,
        );
        return [key, Number.isFinite(parsed) ? parsed : null];
      }),
    );
  }

  const previousBig4 = data?.previous?.cards?.big4 ?? data?.previous?.big4;
  if (!previousBig4 || typeof previousBig4 !== 'object') return {};

  return {
    totalRevenue: percentDelta(
      currentBig4.totalRevenue,
      metricValue(previousBig4.totalRevenue),
    ),
    totalOrders: percentDelta(
      currentBig4.totalOrders,
      metricValue(previousBig4.totalOrders),
    ),
    activeCustomers: percentDelta(
      currentBig4.activeCustomers,
      metricValue(previousBig4.activeCustomers),
    ),
    averageOrder: percentDelta(
      currentBig4.totalOrders ? currentBig4.totalRevenue / currentBig4.totalOrders : 0,
      metricValue(previousBig4.totalOrders)
        ? metricValue(previousBig4.totalRevenue) /
            metricValue(previousBig4.totalOrders)
        : 0,
    ),
  };
}

function normalizeAttention(list) {
  return (Array.isArray(list) ? list : [])
    .map((item) => ({
      code: String(item?.code ?? item?.type ?? ''),
      severity: ['crit', 'warn', 'info'].includes(item?.severity)
        ? item.severity
        : 'warn',
      count: toNumber(item?.count),
      label: item?.label ?? '',
      detail: item?.detail ?? '',
      actionUrl: item?.actionUrl ?? item?.action_url ?? '',
    }))
    .filter((item) => item.code && item.count > 0);
}

function normalizeSummaryCurrency(meta = {}) {
  const rawCode = meta?.currency?.code;
  const rawSymbol = meta?.currency?.symbol;
  const isLegacyPlaceholder =
    rawCode === 'PKR' ||
    rawSymbol === 'PKR' ||
    rawCode === '$' ||
    rawSymbol === '$';

  return {
    ...(meta || {}),
    currency: {
      code: isLegacyPlaceholder
        ? DEFAULT_CURRENCY.title
        : rawCode || DEFAULT_CURRENCY.title,
      symbol: isLegacyPlaceholder
        ? DEFAULT_CURRENCY.symbol
        : rawSymbol || DEFAULT_CURRENCY.symbol,
    },
  };
}

function unwrapSummaryPayload(response) {
  const payload = response?.data ?? response ?? {};
  if (payload?.cards || payload?.charts || payload?.lists) {
    return payload;
  }
  return payload?.data ?? payload;
}

function normalizeSummaryResponse(rawData = {}) {
  const data = rawData || {};
  const cards = data.cards || {};
  const charts = data.charts || {};
  const lists = data.lists || {};
  const merchandiseSalesTotal = toNumber(
    cards?.big4?.merchandiseSalesTotal ??
      lists?.merchandiseSalesTotal ??
      data?.merchandiseSalesTotal,
  );
  const totalRevenue = metricValue(cards?.big4?.totalRevenue);
  const resolvedRevenue =
    totalRevenue > 0 ? totalRevenue : merchandiseSalesTotal;

  const big4 = {
    totalRevenue: resolvedRevenue,
    totalOrders: metricValue(cards?.big4?.totalOrders),
    activeCustomers: metricValue(cards?.big4?.activeCustomers),
    totalProducts: metricValue(cards?.big4?.totalProducts),
  };

  const salesArea = normalizeSalesChartList(charts?.salesArea);
  const ordersBar = Array.isArray(charts?.ordersBar)
    ? charts.ordersBar.map((item) => ({
        period: item?.period || item?.time || item?.date || '',
        value: toNumber(item?.value ?? item?.count ?? item?.total),
      }))
    : [];

  return {
    scope: data.scope || {},
    filters: data.filters || {},
    meta: normalizeSummaryCurrency(data.meta),
    cards: {
      big4,
      status: {
        inProgress: toNumber(cards?.status?.inProgress),
        cancelled: toNumber(cards?.status?.cancelled),
        delivered: toNumber(cards?.status?.delivered),
        outOfStock: toNumber(cards?.status?.outOfStock),
      },
      ops: {
        pendingRepairs: toNumber(cards?.ops?.pendingRepairs),
        overdueRepairs: toNumber(cards?.ops?.overdueRepairs),
        openTradeIns: toNumber(cards?.ops?.openTradeIns),
        awaitingShipment: toNumber(cards?.ops?.awaitingShipment),
      },
    },
    deltas: resolveDeltas(data, big4),
    previous: data?.previous || {},
    // Falls back to the period charts so the tiles still show a shape before
    // the backend attaches dedicated 7-bucket series.
    trends: {
      totalRevenue: metricSeries(
        cards?.big4?.totalRevenue,
        salesArea.map((point) => point.value),
      ),
      totalOrders: metricSeries(
        cards?.big4?.totalOrders,
        ordersBar.map((point) => point.value),
      ),
      activeCustomers: metricSeries(cards?.big4?.activeCustomers, []),
    },
    attention: normalizeAttention(data?.attention),
    charts: {
      salesArea,
      ordersBar,
    },
    lists: {
      topSoldProducts: normalizeTopProductsList(lists?.topSoldProducts),
      topCustomersLtv: normalizeTopCustomersList(lists?.topCustomersLtv),
    },
    activity: {
      recent: Array.isArray(data?.activity?.recent) ? data.activity.recent : [],
    },
    todos: Array.isArray(data?.todos) ? data.todos : [],
    snapshotNote: data?.snapshotNote || '',
    legacy: data?.legacy || {},
  };
}

const initialState = {
  loading: false,
  refreshing: false,
  hasLoaded: false,
  error: '',
  params: DEFAULT_PARAMS,
  summary: DEFAULT_STATE,
};

export const fetchDashboardSummary = createAsyncThunk(
  'dashboard/fetchSummary',
  async (params = {}, { getState }) => {
    const currentParams = getState()?.dashboardSummary?.params || DEFAULT_PARAMS;
    const requestParams = { ...currentParams, ...params, lang: 'en' };
    const summaryResponse = await statisticService.summary(requestParams);
    const summaryData = unwrapSummaryPayload(summaryResponse);
    const normalized = normalizeSummaryResponse(summaryData);

    if (!normalized.charts.salesArea.length) {
      try {
        const salesResponse = await statisticService.ordersCount({
          time: requestParams.time,
          lang: 'en',
        });
        normalized.charts.salesArea = normalizeSalesChartList(
          unwrapApiList(salesResponse),
        );
      } catch {
        // optional legacy chart fallback only
      }
    }

    if (!normalized.lists.topCustomersLtv.length) {
      try {
        const customersResponse = await statisticService.topCustomers({
          time: requestParams.time,
          perPage: requestParams.perPage,
          lang: 'en',
        });
        normalized.lists.topCustomersLtv = normalizeTopCustomersList(
          unwrapApiList(customersResponse),
        );
      } catch {
        // optional legacy list fallback
      }
    }

    if (!normalized.lists.topSoldProducts.length) {
      try {
        const productsResponse = await statisticService.topProducts({
          time: requestParams.time,
          perPage: requestParams.perPage,
          lang: 'en',
        });
        normalized.lists.topSoldProducts = normalizeTopProductsList(
          unwrapApiList(productsResponse),
        );
      } catch {
        // optional legacy list fallback
      }
    }

    if (requestParams.perPage && normalized.lists.topSoldProducts.length) {
      normalized.lists.topSoldProducts = normalized.lists.topSoldProducts.slice(
        0,
        requestParams.perPage,
      );
    }
    if (requestParams.perPage && normalized.lists.topCustomersLtv.length) {
      normalized.lists.topCustomersLtv = normalized.lists.topCustomersLtv.slice(
        0,
        requestParams.perPage,
      );
    }

    return normalized;
  },
);

const dashboardSummarySlice = createSlice({
  name: 'dashboardSummary',
  initialState,
  reducers: {
    setDashboardSummaryParams(state, action) {
      state.params = { ...state.params, ...(action.payload || {}) };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDashboardSummary.pending, (state) => {
      if (state.hasLoaded) {
        state.refreshing = true;
      } else {
        state.loading = true;
      }
    });
    builder.addCase(fetchDashboardSummary.fulfilled, (state, action) => {
      state.loading = false;
      state.refreshing = false;
      state.hasLoaded = true;
      state.summary = action.payload;
      state.error = '';
    });
    builder.addCase(fetchDashboardSummary.rejected, (state, action) => {
      state.loading = false;
      state.refreshing = false;
      state.hasLoaded = true;
      state.error = action.error?.message || 'Failed to load dashboard summary';
    });
  },
});

export const { setDashboardSummaryParams } = dashboardSummarySlice.actions;
export default dashboardSummarySlice.reducer;
